import type { ReactNode } from "react";
import { LinkCopyField } from "@/components/admin/LinkCopyField";
import { OrganisationPicker } from "@/components/admin/OrganisationPicker";
import { TextArea } from "@/components/admin/AdminShell";
import { Button } from "@/components/ui/button";
import { isGlobalManager } from "@/lib/forms/formSource";
import { getPublicFormUrl } from "@/lib/forms/formUrl";
import type { ApiFieldTypeOption } from "@/lib/formBuilder/apiFieldTypes";
import type { FormBuilderDraft } from "@/types/formBuilder";
import { AnswerShareSettings } from "./AnswerShareSettings";
import { ExternalServicePicker } from "./ExternalServicePicker";
import {
  buildFormSchemaExport,
  downloadFormSchemaJson,
} from "./exportFormSchema";
import type { FormBuilderMeta } from "./types";

function formatDateTime(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("ru-RU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function SettingsCard({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-xl border border-slate-200/80 bg-white p-4 shadow-card-lg sm:p-5">
      <h3 className="mb-4 font-onest text-sm font-semibold text-ink">
        {title}
      </h3>
      {children}
    </section>
  );
}

function MetaStrip({
  formId,
  isYandex,
  createdAt,
  updatedAt,
}: {
  formId: number;
  isYandex: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}) {
  const items = [
    { label: "ID", value: String(formId) },
    { label: "Источник", value: isYandex ? "Яндекс" : "CHM" },
    ...(createdAt
      ? [{ label: "Создана", value: formatDateTime(createdAt) }]
      : []),
    ...(updatedAt
      ? [{ label: "Изменена", value: formatDateTime(updatedAt) }]
      : []),
  ];

  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item) => (
        <div
          key={item.label}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200/80 bg-white px-2.5 py-1.5 font-onest text-xs shadow-sm"
        >
          <span className="text-slate-400">{item.label}</span>
          <span className="font-medium text-slate-700">{item.value}</span>
        </div>
      ))}
    </div>
  );
}

interface FormBuilderSettingsProps {
  isNew: boolean;
  isYandex: boolean;
  formId: number | null;
  formMeta: FormBuilderMeta;
  draft: FormBuilderDraft;
  fieldTypes: ApiFieldTypeOption[];
  organisationId: number | null;
  roles: string[];
  canManage: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  onOrganisationChange: (organisationId: number | null) => void;
  onExternalServiceChange: (serviceId: number | null) => void;
  onDraftChange: (patch: Partial<FormBuilderDraft>) => void;
  onDelete: () => void;
  onCopyError?: (message: string) => void;
}

export function FormBuilderSettings({
  isNew,
  isYandex,
  formId,
  formMeta,
  draft,
  fieldTypes,
  organisationId,
  roles,
  canManage,
  isSaving,
  isDeleting,
  onOrganisationChange,
  onExternalServiceChange,
  onDraftChange,
  onDelete,
  onCopyError,
}: FormBuilderSettingsProps) {
  const nativePublicUrl =
    !isNew && formId != null && !isYandex
      ? getPublicFormUrl(formMeta.publicId ?? formId)
      : null;

  const handleExportSchema = () => {
    const payload = buildFormSchemaExport(draft, formMeta, formId, fieldTypes);
    downloadFormSchemaJson(payload, formId);
  };

  return (
    <div className="space-y-4">
      {!isNew && formId != null ? (
        <MetaStrip
          formId={formId}
          isYandex={isYandex}
          createdAt={formMeta.createdAt}
          updatedAt={formMeta.updatedAt}
        />
      ) : null}

      <div className="grid gap-4 xl:grid-cols-2">
        <SettingsCard title="Принадлежит">
          <div className="grid max-w-2xl gap-4 sm:grid-cols-2">
            <div>
              <p className="mb-1 font-onest text-xs font-medium text-slate-700">
                Организация
              </p>
              <OrganisationPicker
                id="form-settings-organisation"
                label=""
                size="compact"
                allowEmpty={isGlobalManager(roles)}
                value={organisationId}
                disabled={!canManage}
                onChange={onOrganisationChange}
              />
            </div>
            {!isYandex && roles.includes("admin") ? (
              <div>
                <p className="mb-1 font-onest text-xs font-medium text-slate-700">
                  Внешний сервис
                </p>
                <ExternalServicePicker
                  value={formMeta.externalServiceId}
                  currentName={formMeta.externalServiceName}
                  disabled={!canManage}
                  onChange={onExternalServiceChange}
                />
              </div>
            ) : null}
          </div>
          {isNew &&
          organisationId == null &&
          canManage &&
          !isGlobalManager(roles) ? (
            <p className="mt-2 font-onest text-xs text-amber-800">
              Нужно выбрать перед сохранением
            </p>
          ) : null}
        </SettingsCard>

        {!isNew ? (
          <SettingsCard title="Сообщение при закрытии">
            <div className="max-w-lg">
              <TextArea
                size="compact"
                value={draft.closedMessage}
                rows={2}
                placeholder="Регистрация завершена"
                onChange={(closedMessage) => {
                  if (canManage) onDraftChange({ closedMessage });
                }}
              />
            </div>
          </SettingsCard>
        ) : null}
      </div>

      {!isNew && formId != null ? (
        <SettingsCard title="Ссылки">
          <div className="max-w-2xl space-y-3">
            {nativePublicUrl ? (
              <LinkCopyField
                label="Публичная ссылка"
                url={nativePublicUrl}
                onCopyError={onCopyError}
              />
            ) : null}
            <AnswerShareSettings
              formId={formId}
              canManage={canManage}
              collapsible
            />
          </div>
        </SettingsCard>
      ) : null}

      {!isNew && isYandex ? (
        <SettingsCard title="Яндекс">
          <div className="space-y-4">
            {formMeta.yandexSurveyId ? (
              <div className="inline-flex flex-wrap items-center gap-1.5 rounded-lg border border-amber-200/80 bg-amber-50/60 px-3 py-2 font-onest text-xs">
                <span className="text-amber-800/70">Survey ID</span>
                <code className="break-all font-mono font-medium text-amber-950">
                  {formMeta.yandexSurveyId}
                </code>
              </div>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-2">
              {formMeta.editorUrl ? (
                <LinkCopyField
                  label="Конструктор"
                  url={formMeta.editorUrl}
                  onCopyError={onCopyError}
                />
              ) : null}
              {formMeta.publicUrl ? (
                <LinkCopyField
                  label="Публичная форма"
                  url={formMeta.publicUrl}
                  onCopyError={onCopyError}
                />
              ) : null}
            </div>
          </div>
        </SettingsCard>
      ) : null}

      {!isNew && !isYandex ? (
        <SettingsCard title="Экспорт">
          <Button
            type="button"
            variant="secondary"
            className="px-4 py-2 text-sm"
            onClick={handleExportSchema}
          >
            Скачать JSON
          </Button>
        </SettingsCard>
      ) : null}

      {!isNew && formId != null ? (
        <div className="pt-1">
          <button
            type="button"
            disabled={isDeleting || isSaving || !canManage}
            onClick={onDelete}
            className="font-onest text-xs text-red-600 transition-colors hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isDeleting ? "Удаление…" : "Удалить форму"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
